import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, FileText, Send } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#0B132B]/15 focus:border-[#C5A059] rounded-none px-4 py-3.5 text-[#0B132B] placeholder:text-slate-400 outline-none transition-colors";

export const Contact = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, language: lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#C5A059]/15 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <SectionLabel testid="contact-label">{t.contact.label}</SectionLabel>
          <h2 data-testid="contact-title" className="font-display font-medium tracking-tight text-4xl sm:text-5xl text-[#0B132B] leading-[1.05] mb-6">
            {t.contact.title}
          </h2>
          <p data-testid="contact-sub" className="text-slate-500 font-light leading-relaxed mb-10 max-w-md">
            {t.contact.sub}
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{t.contact.name} *</label>
                <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
              </div>
              <div>
                <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{t.contact.email} *</label>
                <input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{t.contact.phone}</label>
                <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
              </div>
              <div>
                <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{t.contact.company}</label>
                <input data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">{t.contact.message} *</label>
              <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group inline-flex items-center gap-3 rounded-full bg-[#0B132B] text-white font-medium px-8 py-3.5 hover:bg-[#C5A059] hover:text-[#0B132B] transition-colors disabled:opacity-60"
            >
              {sending ? t.contact.sending : t.contact.submit}
              <Send size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="h-full flex flex-col justify-between gap-10 lg:pl-10 lg:border-l border-[#0B132B]/10">
            <div className="space-y-8">
              <div data-testid="contact-address-block">
                <div className="flex items-center gap-3 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8C713A] mb-3">
                  <MapPin size={14} /> {t.contact.addressLabel}
                </div>
                <p className="font-display text-2xl text-[#0B132B]">Olympiakatu 16</p>
                <p className="text-slate-500 font-light">65100 Vaasa, Finland</p>
              </div>
              <div data-testid="contact-id-block">
                <div className="flex items-center gap-3 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8C713A] mb-3">
                  <FileText size={14} /> {t.contact.idLabel}
                </div>
                <p className="font-display text-2xl text-[#0B132B]">3631579-2</p>
                <p className="text-slate-500 font-light">DKT Business Account Ab Oy</p>
              </div>
            </div>
            <p data-testid="contact-note" className="font-mono2 text-xs text-slate-500 border border-dashed border-[#C5A059]/50 p-4 leading-relaxed">
              {t.contact.note}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
